import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  School as SchoolIcon,
  Payment as PaymentIcon,
  Grade as GradeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const StudentDashboard = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [studentData, setStudentData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [financeData, setFinanceData] = useState({ fees: [], payments: [], statistics: { totalDue: '0.00', totalPaid: '0.00', overdueCount: 0, overdueAmount: '0.00' } });
  const [schedule, setSchedule] = useState({ fullSchedule: [], todaySchedule: [], studentInfo: {} });
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'EMIS School',
    school_logo: null
  });

  useEffect(() => {
    fetchStudentData();
    fetchUserProfile();
    fetchFinanceData();
    fetchSchedule();
    fetchSchoolInfo();
  }, []);

  const fetchStudentData = async () => {
    try {
      const response = await axios.get('student-portal/profile');
      if (response.data.success) {
        setStudentData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch student data:', error);
      enqueueSnackbar('Failed to load student profile', { variant: 'error' });
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('profile');
      if (response.data.success) {
        setUserProfile(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchFinanceData = async () => {
    try {
      const response = await axios.get('student-portal/finance');
      if (response.data.success) {
        // Handle the new data structure with fees, payments, and statistics
        const data = response.data.data;
        setFinanceData({
          fees: data.fees || [],
          payments: data.payments || [],
          statistics: data.statistics || { totalDue: '0.00', totalPaid: '0.00', overdueCount: 0, overdueAmount: '0.00' }
        });
      }
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
      enqueueSnackbar('Failed to load finance data', { variant: 'error' });
      // Set default data structure on error
      setFinanceData({
        fees: [],
        payments: [],
        statistics: { totalDue: '0.00', totalPaid: '0.00', overdueCount: 0, overdueAmount: '0.00' }
      });
    }
  };

  const fetchSchedule = async () => {
    try {
      const response = await axios.get('student-portal/schedule');
      if (response.data.success) {
        // Handle the new data structure with fullSchedule and todaySchedule
        const data = response.data.data;
        setSchedule({
          fullSchedule: data.fullSchedule || [],
          todaySchedule: data.todaySchedule || [],
          studentInfo: data.studentInfo || {}
        });
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
      enqueueSnackbar('Failed to load schedule', { variant: 'error' });
      // Set default data structure on error
      setSchedule({
        fullSchedule: [],
        todaySchedule: [],
        studentInfo: {}
      });
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get('/api/settings/school-info');
      if (response.data.success) {
        setSchoolInfo({
          school_name: response.data.data.school_name || 'EMIS School',
          school_logo: response.data.data.school_logo
        });
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
      // Keep default values on error
      setSchoolInfo({
        school_name: 'EMIS School',
        school_logo: null
      });
    }
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        sx={{ 
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          border: `1px solid ${color}30`,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': {
            boxShadow: onClick ? 3 : 1,
            transform: onClick ? 'translateY(-2px)' : 'none'
          },
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: color }}>
                {value}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {title}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const getInitials = (firstName, lastName, username) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    return username ? username.charAt(0).toUpperCase() : 'S';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Section with Profile */}
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        {/* School Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          {schoolInfo.school_logo ? (
            <img
              src={`http://localhost:3000${schoolInfo.school_logo}`}
              alt="School Logo"
              style={{
                height: 50,
                width: 50,
                objectFit: 'contain',
                marginRight: 16,
                borderRadius: 8,
                border: '2px solid rgba(255,255,255,0.3)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline-block';
              }}
            />
          ) : null}
          <SchoolIcon 
            sx={{ 
              mr: 2, 
              fontSize: 50,
              display: schoolInfo.school_logo ? 'none' : 'inline-block',
              opacity: 0.8
            }} 
          />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', opacity: 0.9 }}>
              {schoolInfo.school_name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Student Portal
            </Typography>
          </Box>
        </Box>

        {/* Student Profile Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mr: 3,
              bgcolor: userProfile?.profile_picture ? 'transparent' : 'rgba(255,255,255,0.2)',
              border: '3px solid rgba(255,255,255,0.3)',
              fontSize: '2rem',
              fontWeight: 'bold'
            }}
            src={userProfile?.profile_picture ? `http://localhost:3000/uploads/profiles/${userProfile.profile_picture}` : undefined}
          >
            {!userProfile?.profile_picture && getInitials(studentData?.first_name, studentData?.last_name, user?.username)}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Welcome, {studentData?.first_name || user?.username}!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
              {studentData?.first_name && studentData?.last_name 
                ? `${studentData.first_name} ${studentData.last_name}` 
                : 'Student'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {studentData?.class_name && (
                <Chip 
                  label={`Class: ${studentData.class_name}${studentData.section_name ? ` - ${studentData.section_name}` : ''}`} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                />
              )}
              {studentData?.student_code && (
                <Chip 
                  label={`ID: ${studentData.student_code}`} 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Dashboard Overview
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Current GPA" 
            value={studentData?.gpa ? parseFloat(studentData.gpa).toFixed(2) : "N/A"} 
            icon={<GradeIcon />} 
            color="#4CAF50" 
            onClick={() => navigate('/student/exam-results')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Pending Fees" 
            value={`$${financeData.statistics?.totalDue || '0.00'}`} 
            icon={<PaymentIcon />} 
            color="#FF9800" 
            onClick={() => navigate('/student/finance')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Attendance" 
            value={`${studentData?.attendance_percentage ? parseFloat(studentData.attendance_percentage).toFixed(2) : '0.00'}%`} 
            icon={<CalendarIcon />} 
            color="#2196F3" 
            onClick={() => navigate('/student/schedule')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Subjects" 
            value={schedule.todaySchedule?.length || 0} 
            icon={<SchoolIcon />} 
            color="#9C27B0" 
            onClick={() => navigate('/student/schedule')}
          />
        </Grid>
      </Grid>


    </Box>
  );
};

export default StudentDashboard;
